import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Paper,
  Divider,
  Stack,
  styled,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useAuth } from "../../hooks";
import { Icons } from "../assets/icon";
import WalletStatCardUpdated from "./WalletStatCardUpdated";
import { NewStyledNetflixButton } from "../Dashboard/Talent/Schedules/EditDeleteAvailability";
import TransactionHistory from "./TransactionHistory";
import { transactionsHistory } from "./DummyTransactions";
import { CreditCardOutlined } from "@mui/icons-material";
import AddCreditCardModal from "./AddDepositMode";
import { enqueueSnackbar } from "notistack";
import ChipsConsolidate from "./ChipsConsolidate";
import RedeemPanel from "./RedeemPanel";
import { Link } from "react-router-dom";
import { useGetTalentEarnings } from "../../services/api/user.api";

export const NewSmallGradientButton = styled(Button)(({ theme }) => ({
  borderRadius: "var(--spacing-2x)",
  padding: "inherit",
  background: "var(--button-gradient)",
  color: "var(--neutral-100)",
  textTransform: "none",
  font: "var(--body-sm) !important",

  [theme.breakpoints.up("md")]: {
    font: "var(--semibold-md)",
  },

  "&:hover": {
    background: "var(--buton-gradient-hover)",
  },
  "&:disabled": {
    background: "var(--button-gradient-disabled)",
    color: "var(--neutral-100)",
    cursor: "not-allowed",
  },
}));

export default function WalletDashboardNew() {
  const { setUser, userInfo } = useAuth();
  const [openDeposit, setOpenDeposit] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [openConsolidateDialog, setOpenConsolidateDialog] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<
    null | (typeof accountBalances)[0]
  >(null);

  console.log(userInfo, "userInfo");

  const [walletData, setWalletData] = useState({
    purchased: Number(userInfo?.wallet_purchase?.available_chips ?? 0),
    earned: Number(userInfo?.wallet_earning?.available_chips ?? 0), // or pull dynamically from userInfo if available
  });

  const { data: talentEarnings } = useGetTalentEarnings({
    key: ["talent-earnings"],
  });

  const [accountBalances, setAccountBalances] = useState(() => {
    return (
      userInfo?.talent?.flatMap((talent: any) => {
        const talentAccount = {
          id: talent.id,
          type: "talent",
          name: talent.username,
          balance: 0, // default, will be updated in useEffect
          profileImage: talent.profile_image,
          status: "Active",
        };

        const gtpAccounts =
          talent?.gtps?.map((gtp: any) => ({
            id: gtp.id,
            type: "gtp",
            name: gtp.name,
            balance: 0, // default for now
            profileImage: gtp.profile_image,
            status: "Active",
          })) || [];

        return [talentAccount, ...gtpAccounts];
      }) || []
    );
  });

  // 💰 Update balances from talentEarnings after data is available
  useEffect(() => {
    const earningsArray = talentEarnings?.data;

    if (!Array.isArray(earningsArray)) return;

    setAccountBalances((prev) =>
      prev.map((acc) => {
        if (acc.type === "talent") {
          const matchedEarning = earningsArray.find(
            (e: any) => e.talent?.id === acc.id
          );

          if (matchedEarning) {
            console.log(
              `🎯 Matched balance for talent ID ${acc.id}:`,
              matchedEarning.chips
            );
            return {
              ...acc,
              balance: matchedEarning.chips,
            };
          }
        }

        if (acc.type === "gtp") {
          const matchedEarning = earningsArray.find(
            (e: any) => e.gtp?.id === acc.id
          );

          if (matchedEarning) {
            console.log(
              `🎯 Matched balance for GTP ID ${acc.id}:`,
              matchedEarning.chips
            );
            return {
              ...acc,
              balance: matchedEarning.chips,
            };
          }
        }

        return acc;
      })
    );
  }, [talentEarnings?.data]);

  const handleOpenConsolidateDialog = (
    profile: (typeof accountBalances)[0]
  ) => {
    setSelectedProfile(profile);
    setOpenConsolidateDialog(true);
  };

  const handleConfirmConsolidate = () => {
    if (selectedProfile) {
      setAccountBalances((prev) =>
        prev.map((acc) =>
          acc.id === selectedProfile.id ? { ...acc, balance: 0 } : acc
        )
      );

      enqueueSnackbar(
        `Successfully consolidated chips from ${selectedProfile.name}`,
        { variant: "success" }
      );

      setWalletData((prev) => ({
        ...prev,
        earned: prev.earned + selectedProfile?.balance,
      }));

      // TODO: call API or logic to consolidate
    }
    setOpenConsolidateDialog(false);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }} bgcolor="#f5f6fa">
      <Box
        display={"flex"}
        flexDirection={"column"}
        mb={2}
        alignItems={"flex-end"}
      >
        <NewStyledNetflixButton
          onClick={() => setOpenDeposit(true)}
          startIcon={<CreditCardOutlined />}
        >
          Add Chips
        </NewStyledNetflixButton>
      </Box>
      {/* Wallet Cards */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <WalletStatCardUpdated
            title="Purchased Wallet"
            icon="💳"
            value={walletData.purchased}
            description="Used for bookings"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <WalletStatCardUpdated
            title="Earned Wallet"
            icon="💼"
            value={walletData.earned}
            description="Eligible for redemption"
            action
          />
        </Grid>
      </Grid>

      {/* Talent Earnings Table */}
      {accountBalances.length > 0 && (
        <Box mt={5}>
          <Typography variant="h6" gutterBottom>
            Earnings by Talent Profiles
          </Typography>
          <Paper variant="outlined">
            <Box sx={{ width: "100%", overflowX: "auto" }}>
              <Table size="small" sx={{ minWidth: 700 }}>
                <TableHead sx={{ backgroundColor: "#f0f0f0" }}>
                  <TableRow>
                    <TableCell
                      sx={{
                        // position: "sticky",
                        // left: 0,
                        // backgroundColor: "#f0f0f0",
                        zIndex: 1,
                        "&.MuiTableCell-stickyHeader": {
                          borderRight: "1px solid #e0e0e0",
                        },
                      }}
                    >
                      S.N.
                    </TableCell>
                    <TableCell>Profile Name</TableCell>
                    <TableCell>Virtual Earned Balance</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {accountBalances.map((profile, idx) => (
                    <TableRow
                      key={idx}
                      component={Link}
                      to={`/wallet/${profile.id}`}
                      sx={{
                        textDecoration: "none",
                        color: "inherit",
                        cursor: "pointer",
                        backgroundColor: "#fff",
                        transition: "background-color 0.2s ease",
                        "&:hover": {
                          backgroundColor: "var(--primary-100)",
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          // position: "sticky",
                          // left: 0,
                          zIndex: 1,
                          "&.MuiTableCell-stickyHeader": {
                            borderRight: "1px solid #e0e0e0",
                            // backgroundColor: "#fff",
                          },

                          // "&:hover": {
                          //   backgroundColor: "var(--primary-100)",
                          // },
                        }}
                      >
                        {idx + 1}
                      </TableCell>

                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Box
                            component="img"
                            src={profile.profileImage}
                            alt={profile.name}
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: "50%",
                              objectFit: "cover",
                            }}
                          />
                          <Box>
                            <Typography variant="body2" fontWeight={500}>
                              {profile.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              ({profile.type})
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Stack direction="row" alignItems="center" gap={1}>
                          <Icons.Coin /> {profile.balance}
                        </Stack>
                      </TableCell>

                      <TableCell>{profile.status}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Paper>
        </Box>
      )}

      {/* Transaction History & Redeem */}
      <Grid container spacing={4} pt={"40px"}>
        <TransactionHistory transactions={transactionsHistory} />

        {/* Redeem Panel */}
        <Grid item xs={12} md={4}>
          <RedeemPanel
            earnedChips={walletData.earned}
            setWalletData={setWalletData}
          />
        </Grid>
      </Grid>

      <AddCreditCardModal
        open={openDeposit}
        onClose={() => setOpenDeposit(false)}
        onSuccess={() => {
          setOpenDeposit(false);
        }}
      />

      <ChipsConsolidate
        setOpenConsolidateDialog={setOpenConsolidateDialog}
        selectedProfile={selectedProfile}
        handleConfirmConsolidate={handleConfirmConsolidate}
        openConsolidateDialog={openConsolidateDialog}
      />
    </Box>
  );
}
